import { type RefObject, useEffect, useRef } from 'react';

interface UseIntersectionObserverOptions {
  /**
   * The root element to use as the viewport for checking visibility.
   * If not provided, uses the browser viewport.
   */
  root?: RefObject<HTMLElement | null>;
  /**
   * Margin around the root. Can be used to trigger intersection before/after the actual intersection.
   * Example: '100px' will trigger 100px before the element enters the viewport.
   * Default: '0px'
   */
  rootMargin?: string;
  /**
   * A number between 0 and 1 indicating what percentage of the target should be visible to trigger.
   * 0 means any pixel visible, 1 means fully visible.
   * Default: 0
   */
  threshold?: number;
  /**
   * Observer switch.
   * Default: true
   */
  enabled?: boolean;
  /**
   * Callback function called when intersection state changes.
   * @param isIntersecting - Whether the element is intersecting with the root
   * @param entry - The full IntersectionObserverEntry object
   */
  onIntersect?: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void;
}

/**
 * A flexible hook for implementing IntersectionObserver patterns.
 * Returns a ref to attach to the element you want to observe.
 *
 * @example
 * // Infinite scroll
 * const sentinelRef = useIntersectionObserver({
 *   root: containerRef,
 *   rootMargin: '100px',
 *   enabled: hasMore && !loading,
 *   onIntersect: (isIntersecting) => {
 *     if (isIntersecting) fetchNextPage();
 *   }
 * });
 *
 * @example
 * // Scroll position detection
 * const topSentinelRef = useIntersectionObserver({
 *   root: containerRef,
 *   onIntersect: (isAtTop) => setShowBackToTop(!isAtTop)
 * });
 */
export function useIntersectionObserver({
  root,
  rootMargin = '0px',
  threshold = 0,
  enabled = true,
  onIntersect,
}: UseIntersectionObserverOptions): RefObject<HTMLDivElement | null> {
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const target = targetRef.current;
    const rootElement = root?.current;

    // Don't observe if disabled or target doesn't exist
    if (!enabled || !target) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && onIntersect) onIntersect(entry.isIntersecting, entry);
      },
      { root: rootElement || null, rootMargin, threshold }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [root, rootMargin, threshold, enabled, onIntersect]);

  return targetRef;
}
